/**
 * Shared data for the "Calendar:" row on the Details step.
 *
 * One owner of `useSharedCalendarPolling` + the derivations both the
 * view (`CalendarField`) and the edit `Select` need (summary name,
 * "(You)" suffix, select options, edit-pencil enablement, loading).
 *
 * Splitting this out of `useDetailsFormSchema` keeps the schema hook
 * readable and lets the native `type: 'select'` field pull its
 * `options` / `onChange` / `loading` straight from here via
 * `fieldProps`.
 */
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMailBox } from '@/hooks/use-mailbox';
import { useSharedCalendarPolling } from '@/pages/settings/SchedulingPreferences/SharedCalendar/useSharedCalendarPolling';
import { useKFlags } from '@/hooks';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import type { SelectOption } from '@kula-ai/components';
import type { ApiReference } from '../../../config/schedule.types';

export interface SharedCal {
  provider_calendar_id?: string;
  name?: string;
  is_primary?: boolean;
  is_default?: boolean;
  status?: string;
}

interface ScheduledCalendar {
  calendar_name?: string;
  provider_calendar_id?: string;
  is_primary?: boolean;
}

export interface CalendarInfoValue {
  provider_calendar_id?: string;
  is_primary?: boolean;
}

interface UseScheduleCalendarDataOptions {
  interviewPlan?: Record<string, unknown>;
  apiReference?: ApiReference;
  /** Live form value of `calendar_info` — drives summary + suffix. */
  calendarInfo?: CalendarInfoValue | null;
}

export interface ScheduleCalendarData {
  /** Whole row should be in the schema at all. */
  shouldShowRow: boolean;

  /** Calendar service is connected in the session. */
  isCalConnected: boolean;

  /** Shared-calendar poll is resolving for the first time. */
  isLoading: boolean;

  /** Raw list of non-deleted shared calendars. */
  calendars: SharedCal[];

  /** Options feed for the edit-mode native `type: 'select'`. */
  selectOptions: SelectOption[];

  /** Name shown in view mode. Empty string when nothing resolves. */
  summaryName: string;

  /** Append " (You)" in view mode. */
  showYouSuffix: boolean;

  /** Hide the edit pencil entirely. */
  hideEdit: boolean;

  /** Disabled-state tooltip on the edit pencil. */
  disabledTooltip?: string;

  /** Default calendar to seed the form with on create. */
  defaultCalendar?: SharedCal;

  /** Lookup helper used by the Select's onChange transform. */
  findById: (_id: string) => SharedCal | undefined;

  /** Forces a re-poll — call before opening the edit dropdown. */
  refetchUuid: () => void;
}

export function useScheduleCalendarData({
  interviewPlan,
  apiReference,
  calendarInfo,
}: UseScheduleCalendarDataOptions): ScheduleCalendarData {
  const isEdit = apiReference === 'edit';
  const sharedEnabled = Boolean(interviewPlan?.shared_calendar_enabled);

  const { feSchedulingPreference } = useKFlags() as {
    feSchedulingPreference?: boolean;
  };
  const currUser = useSelector(selectCurrUser) as
    | { connected_calendar?: { gcal?: boolean; mscal?: boolean } }
    | null
    | undefined;
  const hasConnectedCalendar = Boolean(
    currUser?.connected_calendar?.gcal || currUser?.connected_calendar?.mscal
  );
  const isSharedCalendarEnabled = isEdit || sharedEnabled;
  const shouldShowRow = Boolean(
    feSchedulingPreference && hasConnectedCalendar && isSharedCalendarEnabled
  );

  const { isCalConnected } = useMailBox();

  const { isLoading, sharedCalendarOptions, refetchUuid } =
    useSharedCalendarPolling({
      type: 'saved',
      autoFetch: shouldShowRow && isCalConnected,
    });

  const calendars: SharedCal[] = useMemo(
    () =>
      (sharedCalendarOptions?.calendars as SharedCal[] | undefined)?.filter(
        (c) => c?.status !== 'deleted_from_provider'
      ) ?? [],
    [sharedCalendarOptions]
  );

  const findById = useCallback(
    (id: string) => calendars.find((c) => c.provider_calendar_id === id),
    [calendars]
  );

  const defaultCalendar = useMemo(
    () => calendars.find((c) => c.is_default || c.is_primary),
    [calendars]
  );

  const scheduledCal = interviewPlan?.scheduled_calendar as
    | ScheduledCalendar
    | undefined;

  // Summary for the view strip. Priority:
  //   1. Currently-selected calendar (from fetched list)
  //   2. Edit mode's `scheduled_calendar.calendar_name` (no extra fetch)
  //   3. Create mode's default calendar name
  //   4. Empty — the row shows just "Calendar:" while loading so we
  //      never pretend a fake "Organizer calendar" exists.
  const summaryName = useMemo(() => {
    const id = calendarInfo?.provider_calendar_id;
    if (id) {
      const selected = findById(id);
      if (selected?.name) return selected.name;
    }
    if (isEdit && scheduledCal?.calendar_name) {
      return scheduledCal.calendar_name;
    }
    if (!isEdit && defaultCalendar?.name) return defaultCalendar.name;
    return '';
  }, [
    calendarInfo?.provider_calendar_id,
    defaultCalendar,
    findById,
    isEdit,
    scheduledCal?.calendar_name,
  ]);

  const showYouSuffix = useMemo(() => {
    const id = calendarInfo?.provider_calendar_id;
    const cal = id ? findById(id) : undefined;
    if (cal) return Boolean(cal.is_primary);
    if (isEdit) return Boolean(scheduledCal?.is_primary);
    if (!isEdit && defaultCalendar) return Boolean(defaultCalendar.is_primary);
    return false;
  }, [
    calendarInfo?.provider_calendar_id,
    defaultCalendar,
    findById,
    isEdit,
    scheduledCal?.is_primary,
  ]);

  const hideEdit = isEdit && !sharedEnabled;
  const onlyPrimaryAvailable =
    calendars.length === 0 ||
    (calendars.length === 1 && calendars[0]?.is_primary);
  const disabledTooltip =
    !isLoading && onlyPrimaryAvailable
      ? "You don't have access to any shared calendars"
      : undefined;

  const selectOptions: SelectOption[] = useMemo(
    () =>
      calendars
        .filter(
          (c): c is SharedCal & { provider_calendar_id: string } =>
            typeof c.provider_calendar_id === 'string'
        )
        .map((c) => ({
          value: c.provider_calendar_id,
          label: `${c.name ?? c.provider_calendar_id}${c.is_primary ? ' (You)' : ''}`,
        })),
    [calendars]
  );

  return {
    shouldShowRow,
    isCalConnected,
    isLoading,
    calendars,
    selectOptions,
    summaryName,
    showYouSuffix,
    hideEdit,
    disabledTooltip,
    defaultCalendar,
    findById,
    refetchUuid,
  };
}
